import { convertToCamelCase, convertToSnakeCase } from "../utils/caseUtils";
import api from "../utils/smartApiClient";

const ripAPIPrefix = "/api/rip/v1";

// Site APIs
export const fetchSites = async (acctId) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/sites/${acctId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch sites:", error);
    throw new Error("Failed to fetch sites");
  }
};

export const fetchDetectionProducts = async (acctId) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/detections/products/${acctId}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch detection products:", error);
    throw new Error("Failed to fetch detection products");
  }
};

// Scan APIs
export const fetchScanSummary = async (acctId) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/scans/summary/${acctId}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch scan summary:", error);
    throw new Error("Failed to fetch scan summary");
  }
};

export const fetchTrains = async (acctId, lastTrainId) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/scans/trains/${acctId}/${lastTrainId || 0}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch trains:", error);
    throw new Error("Failed to fetch trains");
  }
};

export const fetchTrainById = async (trainID, acctID) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/scans/train/${acctID}/${trainID}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to fetch train with ID ${trainID}:`, error);
    throw new Error(`Failed to fetch train with ID ${trainID}`);
  }
}

export const fetchRailcars = async (acctId, lastRailcarId) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/scans/railcars/${acctId}/${lastRailcarId || 0}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch railcars:", error);
    throw new Error("Failed to fetch railcars");
  }
};

export const fetchRailcarById = async (railcarID) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/railcars/${railcarID}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to fetch railcar with ID ${railcarID}:`, error);
    throw new Error(`Failed to fetch railcar with ID ${railcarID}`);
  }
};

export const fetchDetections = async (acctId, lastDetectionId) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/scans/detections/${acctId}/${lastDetectionId || 0}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch detections:", error);
    throw new Error("Failed to fetch detections");
  }
};

// Track Module APIs
export const fetchAllTrackModules = async () => {
  try {
    const response = await api.get(`${ripAPIPrefix}/track-modules`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch track modules:", error);
    throw new Error("Failed to fetch track modules");
  }
};

export const fetchTrackModuleById = async (id) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/track-modules/${id}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to fetch track module with ID ${id}:`, error);
    throw new Error(`Failed to fetch track module with ID ${id}`);
  }
};

export const fetchTrackModulesByTrackId = async (trackId) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/track-modules/track/${trackId}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to fetch track modules for track ID ${trackId}:`, error);
    throw new Error(`Failed to fetch track modules for track ID ${trackId}`);
  }
};

export const createTrackModule = async (trackModuleData) => {
  try {
    const response = await api.post(
      `${ripAPIPrefix}/track-modules`,
      convertToSnakeCase(trackModuleData),
      {
        headers: { "Content-Type": "application/json" }
      }
    );
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to create track module:", error);
    throw new Error("Failed to create track module");
  }
};

export const updateTrackModules = async (trackId, trackModuleData) => {
  try {
    const response = await api.put(
      `${ripAPIPrefix}/track-modules/update-track/${trackId}`,
      convertToSnakeCase(trackModuleData)
    );
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to update track modules:", error);
    throw new Error("Failed to update track modules");
  }
};

export const updateTrackModule = async (id, trackModuleData) => {
  try {
    const response = await api.put(
      `${ripAPIPrefix}/track-modules/${id}`,
      trackModuleData,
      {
        headers: { "Content-Type": "application/json" }
      }
    );
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to update track module with ID ${id}:`, error);
    throw new Error(`Failed to update track module with ID ${id}`);
  }
};

export const deleteTrackModule = async (id) => {
  try {
    await api.delete(`${ripAPIPrefix}/track-modules/${id}`);
    return true;
  } catch (error) {
    console.error(`Failed to delete track module with ID ${id}:`, error);
    throw new Error(`Failed to delete track module with ID ${id}`);
  }
};

export const fetchTrainImages = async (trainId, acctID, payload) => {
  try {
    const response = await api.post(
      `${ripAPIPrefix}/scans/images/${acctID}/${trainId}`,
      payload
    );
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to fetch images for train ID ${trainId}:`, error);
    throw new Error(`Failed to fetch images for train ID ${trainId}`);
  }
};

// Railcar Inspection APIs
export const fetchRailcarInspections = async (acctId) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/scan-railcar-inspections/acct/${acctId}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch railcar inspections:", error);
    throw new Error("Failed to fetch railcar inspections");
  }
};

export const fetchRailcarInspectionById = async (id) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/scan-railcar-inspections/${id}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to fetch railcar inspection with ID ${id}:`, error);
    throw new Error(`Failed to fetch railcar inspection with ID ${id}`);
  }
};

export const fetchRailcarInspectionByUID = async (uid) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/scan-railcar-inspections/uid/${uid}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to fetch railcar inspection with UID ${uid}:`, error);
    throw new Error(`Failed to fetch railcar inspection with UID ${uid}`);
  }
};

export const createRailcarInspection = async (inspectionData) => {
  try {
    const response = await api.post(
      `${ripAPIPrefix}/scan-railcar-inspections`,
      convertToSnakeCase(inspectionData),
      {
        headers: { "Content-Type": "application/json" }
      }
    );
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to create railcar inspection:", error);
    throw new Error("Failed to create railcar inspection");
  }
};

export const updateRailcarInspection = async (id, inspectionData) => {
  try {
    const response = await api.put(
      `${ripAPIPrefix}/scan-railcar-inspections/${id}`,
      convertToSnakeCase(inspectionData),
      {
        headers: { "Content-Type": "application/json" }
      }
    );
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to update railcar inspection with ID ${id}:`, error);
    throw new Error(`Failed to update railcar inspection with ID ${id}`);
  }
};

export const deleteRailcarInspection = async (id) => {
  try {
    await api.delete(`${ripAPIPrefix}/scan-railcar-inspections/${id}`);
    return true;
  } catch (error) {
    console.error(`Failed to delete railcar inspection with ID ${id}:`, error);
    throw new Error(`Failed to delete railcar inspection with ID ${id}`);
  }
};

export const addInspectionComment = async (inspectionId, commentText, createdBy) => {
  try {
    const commentData = {
      inspection_id: inspectionId,
      inspection_comment: commentText,
      created_by: createdBy
    };
    
    const response = await api.post(
      `${ripAPIPrefix}/scan-railcar-inspections/${inspectionId}/comments`,
      commentData,
      {
        headers: { "Content-Type": "application/json" }
      }
    );
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to add comment to inspection ${inspectionId}:`, error);
    throw new Error(`Failed to add comment to inspection`);
  }
};

export const fetchRailcarInspectionByScanRailcarId = async (scanRailcarId) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/scan-railcar-inspections/scan-railcar/${scanRailcarId}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to fetch railcar inspection for scan railcar ID ${scanRailcarId}:`, error);
    throw new Error(`Failed to fetch railcar inspection for scan railcar ID ${scanRailcarId}`);
  }
};

export const uploadInspectionImage = async (imageData) => {
  try {
    const response = await api.post(`${ripAPIPrefix}/scan-railcar-inspections/image`, imageData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error uploading inspection image:', error);
    throw error;
  }
};

export const updateInspectionImage = async (updateInfo) => {
  try {
    const response = await api.put(`${ripAPIPrefix}/scan-railcar-inspections/image`, updateInfo, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error updating inspection image:', error);
    throw error;
  }
};

export const removeTemporaryImage = async (imagePath) => {
  try {
    const response = await api.delete(`${ripAPIPrefix}/scan-railcar-inspections/image/temporary`, {
      data: { current_path: imagePath },
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error removing temporary image:', error);
    throw error;
  }
};

export const getDetectionByID = async (id, acctId) => {
  try {
    // Include acct_id in URL path if provided, otherwise use 0 as placeholder
    const acctIdSegment = acctId || 0;
    const url = `${ripAPIPrefix}/scan-railcar-detections/${acctIdSegment}/${id}`;
    const response = await api.get(url);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to fetch detection with ID ${id}:`, error);
    throw new Error(`Failed to fetch detection with ID ${id}`);
  }
};

// Detection Validation APIs
export const updateDetectionValidationStatus = async (detectionId, validationStatus, lastValidatedBy, acctId, validationNotes) => {
  try {
    const response = await api.put(`${ripAPIPrefix}/scan-railcar-detections/${detectionId}/validate`, 
      convertToSnakeCase({
        validationStatus,
        lastValidatedBy,
        acctId,
        validationNotes
      })
    );
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to update validation status for detection ${detectionId}:`, error);
    throw new Error(`Failed to update validation status: ${error.message}`);
  }
};

export const getDetectionValidationHistory = async (detectionId, acctId) => {
  try {
    const response = await api.get(`${ripAPIPrefix}/scan-railcar-detections/${acctId}/${detectionId}/validation-history`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error(`Failed to fetch validation history for detection ${detectionId}:`, error);
    throw new Error(`Failed to fetch validation history: ${error.message}`);
  }
};

